import languages from '@/assets/languages.json';
import SettingsChipsField from '@/components/settings/SettingsChipsField.tsx';
import { SettingTitle } from '@/components/settings/SettingsTitle.tsx';
import { DashboardContext } from '@/contexts/DashboardContext.tsx';
import { AudienceConfig } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import Label from '@superfunnel/ui-core/v2/Label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@superfunnel/ui-core/v2/Select';
import TextArea from '@superfunnel/ui-core/v2/TextArea';
import { useContext, useEffect, useState } from 'react';

export const AudiencePersonalizationSettings = () => {
  const { accountAiSettings, updateAccountAiSettings } =
    useContext(DashboardContext);
  const [edit, setEdit] = useState<boolean>(false);
  const [audience, setAudience] = useState<Partial<AudienceConfig>>({
    personaDescription: '',
    excludedAudiences: [],
    locale: 'en-US',
  });

  // Initialize from context
  useEffect(() => {
    if (accountAiSettings?.audience) {
      setAudience(accountAiSettings.audience);
    }
  }, [accountAiSettings?.audience]);

  // Check for changes to show/hide save button
  useEffect(() => {
    if (!accountAiSettings?.audience) {
      setEdit(
        audience.personaDescription !== '' ||
          (audience.excludedAudiences?.length ?? 0) > 0 ||
          audience.locale !== 'en-US',
      );
      return;
    }

    const current = accountAiSettings.audience;
    setEdit(
      audience.personaDescription !== current.personaDescription ||
        JSON.stringify(audience.excludedAudiences?.sort()) !==
          JSON.stringify(current.excludedAudiences?.sort()) ||
        audience.locale !== current.locale,
    );
  }, [audience, accountAiSettings?.audience]);

  const onSave = () => {
    setEdit(false);
    updateAccountAiSettings({ audience });
  };

  const onDiscard = () => {
    setEdit(false);
    if (accountAiSettings?.audience) {
      setAudience(accountAiSettings.audience);
    } else {
      setAudience({
        personaDescription: '',
        excludedAudiences: [],
        locale: 'en-US',
      });
    }
  };

  return (
    <div
      className={
        'relative grid h-full w-full max-w-full grid-rows-[1fr_auto] overflow-hidden'
      }
    >
      <div className={'h-full overflow-y-auto'}>
        <div className={'mx-auto flex max-w-161 flex-col gap-5 px-2.5'}>
          <SettingTitle
            title="Audience & Personalization"
            description="Define your target personas and personalization settings for AI-generated content."
          />

          <div className="flex flex-col gap-5">
            <div className="space-y-2">
              <Label className="text-base-700 text-sm font-medium">
                Persona / ICP
              </Label>
              <p className="mb-2 text-sm text-gray-600">
                Describe your ideal customer profile, including demographics,
                pain points, and key characteristics
              </p>
              <TextArea
                value={audience.personaDescription || ''}
                onChange={(e) =>
                  setAudience({
                    ...audience,
                    personaDescription: e.target.value,
                  })
                }
                placeholder="Describe your ideal customer persona: who they are, their role, pain points, goals, and key characteristics..."
                rows={6}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-base-700 text-sm font-medium">
                Excluded audiences
              </Label>
              <p className="mb-2 text-sm text-gray-600">
                Audiences to avoid targeting (e.g., Under 18, Competitors)
              </p>
              <SettingsChipsField
                selected={
                  audience.excludedAudiences?.map((aud) => ({
                    id: aud,
                    title: aud,
                  })) || []
                }
                setSelected={(selected) =>
                  setAudience({
                    ...audience,
                    excludedAudiences: selected.map((s) => s.title),
                  })
                }
                options={[]}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-base-700 text-sm font-medium">
                Region & spelling
              </Label>
              <Select
                value={audience.locale || 'en-US'}
                onValueChange={(value) =>
                  setAudience({ ...audience, locale: value })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map((language) => (
                    <SelectItem key={language.code} value={language.code}>
                      {language.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
      {edit && (
        <div
          className={
            'mx-auto flex w-full max-w-161 justify-end gap-3 bg-white px-2.5 pt-2'
          }
        >
          <Button variant={'outline'} onClick={onDiscard}>
            Discard
          </Button>
          <Button onClick={onSave}>Save</Button>
        </div>
      )}
    </div>
  );
};
